import { Chip, TableCell, TableRow, Typography } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

//components
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
import { Alert } from 'components/common/Alert';
import { ConsentDialog } from 'components/common/ConsentDialog';
import NoDataFound from 'components/common/NoDataFound';
import TableComponent from 'components/common/TableComponent';
import TableContainer from 'components/common/TableContainer';
import TableLoader from 'components/common/TableLoader';
import TableSearchField from 'components/common/TableSearchField';
import TableTabsComponent from 'components/common/TableTabsComponent';

//constants, reducers, graphql, styles
import {
  ACTIVE_TEXT,
  DEACTIVATION_WARNING_TEXT,
  HTTP_STATUS,
  INACTIVE_TEXT,
  LOADING_TABLE_ROWS,
  STAFF_TABLE_HEADER,
  STAFF_TABS,
  STAFF_VIEW_ROUTE,
  TAB_QUERY_TYPE,
  WARNING_TEXT,
} from 'constants/index';
import {
  UserStatus,
  UsersPayload,
  useFindAllStaffLazyQuery,
  useUpdateUserStatusMutation,
} from 'generated/graphql';
import { StaffTabType } from 'interfaces/TabTypes';
import { formatPhone, formatTimeStamp } from 'lib/helper';
import { Action, ActionType, State, initialState, staffReducer } from 'reducer/staffReducer';
import { textWhiteSpace } from 'styles/commonComponentStyle';

const StaffTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(staffReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab } = state;
  const [showCountdown, setShowCountdown] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [searchParams] = useSearchParams();
  const tab = searchParams.get(TAB_QUERY_TYPE);

  const [findAllStaff, { loading: findAllStaffLoading, error, refetch }] = useFindAllStaffLazyQuery({
    onCompleted: (data) => {
      const { findAllStaff } = data;
      const { pagination, response, data: staffData } = findAllStaff || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({ type: ActionType.SET_DATA, data: staffData as UsersPayload['data'] });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const [updateUserStatus, { loading: updateUserStatusLoading }] = useUpdateUserStatusMutation({
    onCompleted: (data) => {
      const { updateUserStatus } = data;
      const { response } = updateUserStatus || {};
      const { message, status } = response || {};

      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        handleClose();
      } else {
        Alert.error(message ?? '');
      }
      refetch();
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchStaff = useCallback(async () => {
    await findAllStaff({
      variables: {
        findAllStaffInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          search,
          ...(activeTab !== 'all' && { status: activeTab }),
        },
      },
    });
  }, [findAllStaff, search, rowsPerPage, page, activeTab]);

  useEffect(() => {
    (!search.length || search.length > 0) && fetchStaff();
  }, [fetchStaff, search.length]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as StaffTabType });
  };

  const ChangeStaffStatus = async (status: UserStatus, id: string) => {
    await updateUserStatus({
      variables: {
        updateUserStatusInput: {
          id: id,
          status,
        },
      },
    });
  };

  const onActiveHandler = async (id: string, status: boolean): Promise<void> => {
    setSelectedId(id);
    if (status) {
      setShowCountdown(true);
    } else {
      ChangeStaffStatus(UserStatus.Active, id);
    }
  };

  const handleStaffStatus = async () => {
    ChangeStaffStatus(UserStatus.Inactive, selectedId);
  };

  const handleClose = () => {
    setShowCountdown(false);
    setSelectedId('');
  };

  useMemo(() => {
    if (tab === 'all') dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: 'all' });
  }, [tab]);

  const loading = findAllStaffLoading || updateUserStatusLoading;

  const noData = Boolean((!findAllStaffLoading && data?.length === 0) || error);

  return (
    <Fragment>
      <ConsentDialog
        isError
        countDown
        open={showCountdown}
        onClose={handleClose}
        onCountDownComplete={handleStaffStatus}
        loading={false}
        headerText={WARNING_TEXT}>
        <Typography variant="subtitle1" mt={1}>
          {DEACTIVATION_WARNING_TEXT}
        </Typography>
      </ConsentDialog>
      <TableContainer>
        <TableTabsComponent activeTab={activeTab} tabsList={STAFF_TABS} tabHandler={tabHandler} />

        <TableSearchField
          search={search}
          onClear={() => dispatch({ type: ActionType.SET_SEARCH, search: '' })}
          onChange={(val) => dispatch({ type: ActionType.SET_SEARCH, search: val })}
        />

        <TableComponent
          noData={noData}
          page={page - 1}
          count={count}
          rowsPerPage={rowsPerPage}
          tableHeader={STAFF_TABLE_HEADER}
          setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
          setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
          {loading ? (
            <TableLoader columns={7} rows={LOADING_TABLE_ROWS} />
          ) : (
            <Fragment>
              {data?.map((cell) => {
                const { id, email, firstName, lastName, phone, createdAt, staffRole, status } = cell || {};

                return (
                  <TableRow key={id}>
                    <TableCell sx={textWhiteSpace}>
                      {firstName ?? '-'} {lastName ?? '-'}
                    </TableCell>
                    <TableCell sx={textWhiteSpace}>{staffRole ?? '--'}</TableCell>
                    <TableCell>{email ?? '--'}</TableCell>
                    <TableCell sx={textWhiteSpace}>{formatPhone(phone || '')}</TableCell>
                    <TableCell>{formatTimeStamp(createdAt, 'MM/DD/YY')}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={status === UserStatus.Active ? ACTIVE_TEXT : INACTIVE_TEXT}
                        color={status === UserStatus.Active ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <ActionMenuDropdown
                        id={id ?? ''}
                        isActive={status === UserStatus.Active}
                        viewRoute={STAFF_VIEW_ROUTE}
                        onActiveHandler={onActiveHandler}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </Fragment>
          )}
        </TableComponent>

        <NoDataFound noData={noData} />
      </TableContainer>
    </Fragment>
  );
};

export default StaffTable;
